import { Button } from "@/components/ui/button";
import { Shield, CheckCircle, Clock, Users, ArrowRight } from "lucide-react";
import { track } from "@/lib/analytics";

export function ZorgeloosSection() {
  return (
    <section className="py-16 bg-gradient-to-br from-background to-muted/20">
      <div className="container mx-auto px-4">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-accent/10 text-accent px-4 py-2 rounded-full text-sm font-semibold mb-4">
              <Shield className="h-4 w-4" />
              Zorgeloos & Boetevrij
            </div>
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Wij Regelen <span className="text-accent">EasyStroom Voordeel</span> Voor U
            </h2>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
              Energieleveranciers gaan boetes rekenen voor teruglevering. EasyStroom doet dit NIET! 
              SOL-VI klanten krijgen exclusieve toegang tot EasyStroom zonder boetes.
            </p>
          </div>

          {/* Main Content Grid */}
          <div className="grid md:grid-cols-2 gap-8 mb-12">
            {/* Problem/Solution */}
            <div className="bg-white p-8 rounded-xl border shadow-sm">
              <h3 className="text-xl font-semibold mb-4 text-red-600">
                 <span className="inline-flex items-center gap-2">
                   <span className="inline-flex items-center justify-center w-5 h-5 bg-red-600 text-white rounded text-xs">⚠</span>
                   Energieleveranciers Gaan Boetes Rekenen
                 </span>
              </h3>
              <ul className="space-y-3 mb-6">
                <li className="flex items-start gap-2">
                  <div className="w-2 h-2 bg-red-500 rounded-full mt-2 flex-shrink-0"></div>
                  <span className="text-sm">Boetes voor teruggeleverde zonnestroom bij meeste leveranciers</span>
                </li>
                <li className="flex items-start gap-2">
                  <div className="w-2 h-2 bg-red-500 rounded-full mt-2 flex-shrink-0"></div>
                  <span className="text-sm">Boetes kunnen oplopen tot €100+ per jaar</span>
                </li>
                <li className="flex items-start gap-2">
                  <div className="w-2 h-2 bg-red-500 rounded-full mt-2 flex-shrink-0"></div>
                  <span className="text-sm">Traditionele leveranciers maken zonnepanelen minder rendabel</span>
                </li>
              </ul>
            </div>

            {/* SOL-VI Solution */}
            <div className="bg-gradient-to-br from-accent/5 to-accent/10 p-8 rounded-xl border border-accent/20">
              <h3 className="text-xl font-semibold mb-4 text-accent">
                 <span className="inline-flex items-center gap-2">
                   <CheckCircle className="w-5 h-5" />
                   SOL-VI + EasyStroom Samenwerking
                 </span>
              </h3>
              <ul className="space-y-3 mb-6">
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-accent mt-0.5 flex-shrink-0" />
                  <span className="text-sm">EasyStroom rekent GEEN boetes voor zonnepanelen</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-accent mt-0.5 flex-shrink-0" />
                  <span className="text-sm">Maximaal rendement van uw zonne-investering</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-accent mt-0.5 flex-shrink-0" />
                  <span className="text-sm">Exclusieve deal: alleen voor SOL-VI klanten</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-accent mt-0.5 flex-shrink-0" />
                  <span className="text-sm">Dynamische tarieven: profiteer van lage energieprijzen (€0,07/kWh)</span>
                </li>
              </ul>
            </div>
          </div>

          {/* Timeline */}
          <div className="bg-muted/30 p-8 rounded-xl mb-8">
            <h3 className="text-xl font-semibold mb-6 text-center">Wat Verandert Er Wanneer?</h3>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="bg-accent text-white w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3 font-bold">
                  2024
                </div>
                <h4 className="font-semibold mb-2">Eerste Boetes</h4>
                <p className="text-sm text-muted-foreground">Eerste leveranciers introduceren boetes voor teruglevering</p>
              </div>
              <div className="text-center">
                <div className="bg-orange-500 text-white w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3 font-bold">
                  2025
                </div>
                <h4 className="font-semibold mb-2">Meer Leveranciers</h4>
                <p className="text-sm text-muted-foreground">Meer leveranciers volgen - EasyStroom houdt geen boetes</p>
              </div>
              <div className="text-center">
                <div className="bg-red-500 text-white w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3 font-bold">
                  2026+
                </div>
                <h4 className="font-semibold mb-2">EasyStroom Voordeel</h4>
                <p className="text-sm text-muted-foreground">EasyStroom houdt 'geen boetes' belofte voor SOL-VI klanten</p>
              </div>
            </div>
          </div>

          {/* Guarantees */}
          <div className="grid md:grid-cols-3 gap-6 mb-8">
            <div className="text-center p-6 bg-white rounded-lg border">
              <Shield className="h-8 w-8 text-accent mx-auto mb-3" />
              <h4 className="font-semibold mb-2">100% Compliant</h4>
              <p className="text-sm text-muted-foreground">Volledige naleving nieuwe regelgeving</p>
            </div>
            <div className="text-center p-6 bg-white rounded-lg border">
              <Clock className="h-8 w-8 text-accent mx-auto mb-3" />
              <h4 className="font-semibold mb-2">Zorgeloos</h4>
              <p className="text-sm text-muted-foreground">Wij regelen alle administratie voor u</p>
            </div>
            <div className="text-center p-6 bg-white rounded-lg border">
              <Users className="h-8 w-8 text-accent mx-auto mb-3" />
              <h4 className="font-semibold mb-2">Expert Support</h4>
              <p className="text-sm text-muted-foreground">Persoonlijke begeleiding door specialisten</p>
            </div>
          </div>

          {/* CTA */}
          <div className="text-center">
            <Button 
              size="lg" 
              variant="cta" 
              className="text-lg px-8"
              onClick={() => { 
                track("zorgeloos_section_calendly"); 
                (window as any).Calendly?.initPopupWidget?.({
                  url: 'https://calendly.com/sol-vi/new-meeting?primary_color=ff7800',
                  pageSettings: {
                    backgroundColor: 'ffffff',
                    hideEventTypeDetails: false,
                    hideLandingPageDetails: false,
                    primaryColor: 'ff7800',
                    textColor: '4d5055'
                  }
                })
              }}
            >
              Plan Gratis Zorgeloos Gesprek
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            <p className="text-sm text-muted-foreground mt-2">
              Geen verplichtingen • Direct duidelijkheid • Expert advies
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}